/**
 * 客服实时通道客户端（连接 /support 命名空间）
 * 单例；连接幂等；发送前确保已连接；监听器可按引用移除，避免 StrictMode 双挂导致的竞态。
 */

import { io, Socket } from 'socket.io-client';
import type { SupportMessage } from './supportApi';

type MessageHandler = (msg: SupportMessage) => void;
type HistoryHandler = (data: { conversationId: string; messages: SupportMessage[] }) => void;
type ErrorHandler = (err: { code: string; message: string }) => void;
type JoinedHandler = (data: { conversationId: string }) => void;

class SupportSocketClient {
  private socket: Socket | null = null;

  private resolveUrl(): string {
    let url = (import.meta.env.VITE_SOCKET_URL as string) || '/';
    if (url === '/') url = window.location.origin;
    return url;
  }

  /** 获取（或创建）socket 实例；幂等，始终返回同一实例（token 更新时重建） */
  private ensureSocket(): Socket {
    const token = localStorage.getItem('accessToken') || '';
    if (this.socket) {
      // token 变了（重新登录）则重建
      if ((this.socket.auth as any)?.token !== token) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      } else {
        return this.socket;
      }
    }
    this.socket = io(`${this.resolveUrl()}/support`, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    return this.socket;
  }

  connect(): void {
    const s = this.ensureSocket();
    if (!s.connected) s.connect();
  }

  /** 仅在登出/整体结束时调用；组件卸载不要调用（否则 StrictMode 双挂会断连） */
  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /** 确保连接后执行 fn（已连立即执行，否则等一次 connect 事件） */
  private whenConnected(fn: () => void): void {
    const s = this.ensureSocket();
    if (s.connected) { fn(); return; }
    s.once('connect', fn);
    if (!s.connected) s.connect();
  }

  join(conversationId?: string): void {
    this.whenConnected(() => this.socket?.emit('support:join', { conversationId }));
  }

  requestHistory(conversationId: string, before?: string, limit = 50): void {
    this.whenConnected(() => this.socket?.emit('support:history', { conversationId, before, limit }));
  }

  sendText(conversationId: string, content: string): void {
    this.whenConnected(() =>
      this.socket?.emit('support:send', { conversationId, contentType: 'text', content })
    );
  }

  sendImage(conversationId: string, imageUrl: string): void {
    this.whenConnected(() =>
      this.socket?.emit('support:send', { conversationId, contentType: 'image', imageUrl })
    );
  }

  onMessage(handler: MessageHandler): void { this.ensureSocket().on('support:message', handler); }
  offMessage(handler: MessageHandler): void { this.socket?.off('support:message', handler); }

  onHistory(handler: HistoryHandler): void { this.ensureSocket().on('support:history', handler); }
  offHistory(handler: HistoryHandler): void { this.socket?.off('support:history', handler); }

  onJoined(handler: JoinedHandler): void { this.ensureSocket().on('support:joined', handler); }
  offJoined(handler: JoinedHandler): void { this.socket?.off('support:joined', handler); }

  onError(handler: ErrorHandler): void { this.ensureSocket().on('support:error', handler); }
  offError(handler: ErrorHandler): void { this.socket?.off('support:error', handler); }

  onConnect(handler: () => void): void { this.ensureSocket().on('connect', handler); }
}

export const supportSocket = new SupportSocketClient();
export default SupportSocketClient;
